/**
 * Custom 3D editing icons — purpose-built SVG icons for mesh editing,
 * sculpting, and curve operations.
 *
 * Style guide (matches Lucide's visual weight):
 *   • 24×24 viewBox, 2 px stroke, round caps & joins, currentColor
 *   • Opacity 0.3 for "ghost" / before-state geometry
 *   • Filled circles (r ≈ 1.5–2.5) for vertex markers
 *   • Minimal paths — every stroke must read clearly at 13 px
 *
 * Each export is LucideIcon-compatible (forwardRef, accepts LucideProps)
 * so it can be used anywhere a Lucide icon is expected.
 *
 * Source of truth: one SVG file per icon in ./icons/*.svg. Edit those files
 * directly in any SVG editor, or hand-edit the XML — no regeneration step.
 * vite-plugin-svgr turns each SVG into a React component at build time.
 */

import type { LucideProps } from "lucide-react";
import { type ComponentType, forwardRef, type SVGProps } from "react";
import AltIconSvg from "./icons/AltIcon.svg?react";
import BevelIconSvg from "./icons/BevelIcon.svg?react";
import BrushDrawIconSvg from "./icons/BrushDrawIcon.svg?react";
import BrushFlattenIconSvg from "./icons/BrushFlattenIcon.svg?react";
import BrushGrabIconSvg from "./icons/BrushGrabIcon.svg?react";
import BrushHardnessIconSvg from "./icons/BrushHardnessIcon.svg?react";
import BrushInflateIconSvg from "./icons/BrushInflateIcon.svg?react";
import BrushMaskIconSvg from "./icons/BrushMaskIcon.svg?react";
import BrushOpacityIconSvg from "./icons/BrushOpacityIcon.svg?react";
import BrushPinchIconSvg from "./icons/BrushPinchIcon.svg?react";
import BrushRelaxIconSvg from "./icons/BrushRelaxIcon.svg?react";
import BrushSizeIconSvg from "./icons/BrushSizeIcon.svg?react";
import BrushSmoothIconSvg from "./icons/BrushSmoothIcon.svg?react";
import BrushStrengthIconSvg from "./icons/BrushStrengthIcon.svg?react";
import CheckerSelectIconSvg from "./icons/CheckerSelectIcon.svg?react";
import ClearSelectionIconSvg from "./icons/ClearSelectionIcon.svg?react";
import ConnectPathIconSvg from "./icons/ConnectPathIcon.svg?react";
import EdgeLoopIconSvg from "./icons/EdgeLoopIcon.svg?react";
import EdgeModeIconSvg from "./icons/EdgeModeIcon.svg?react";
import EdgeSlideIconSvg from "./icons/EdgeSlideIcon.svg?react";
import ExtrudeIconSvg from "./icons/ExtrudeIcon.svg?react";
import FaceModeIconSvg from "./icons/FaceModeIcon.svg?react";
import FillIconSvg from "./icons/FillIcon.svg?react";
import FlipNormalsIconSvg from "./icons/FlipNormalsIcon.svg?react";
import GridFillIconSvg from "./icons/GridFillIcon.svg?react";
import HandleAlignedIconSvg from "./icons/HandleAlignedIcon.svg?react";
import HandleAutoIconSvg from "./icons/HandleAutoIcon.svg?react";
import HandleFreeIconSvg from "./icons/HandleFreeIcon.svg?react";
import HandleVectorIconSvg from "./icons/HandleVectorIcon.svg?react";
import InsetIconSvg from "./icons/InsetIcon.svg?react";
import InvertSelectionIconSvg from "./icons/InvertSelectionIcon.svg?react";
import MatcapIconSvg from "./icons/MatcapIcon.svg?react";
import MaterialIconSvg from "./icons/MaterialIcon.svg?react";
import MergeVerticesIconSvg from "./icons/MergeVerticesIcon.svg?react";
import MeshMenuIconSvg from "./icons/MeshMenuIcon.svg?react";
import MultiResIconSvg from "./icons/MultiResIcon.svg?react";
import OrientationIconSvg from "./icons/OrientationIcon.svg?react";
import PenPressureIconSvg from "./icons/PenPressureIcon.svg?react";
import PivotIconSvg from "./icons/PivotIcon.svg?react";
import RandomSelectIconSvg from "./icons/RandomSelectIcon.svg?react";
import RecalcOutsideIconSvg from "./icons/RecalcOutsideIcon.svg?react";
import RipIconSvg from "./icons/RipIcon.svg?react";
import SelectAllIconSvg from "./icons/SelectAllIcon.svg?react";
import SelectConnectedIconSvg from "./icons/SelectConnectedIcon.svg?react";
import SelectLessIconSvg from "./icons/SelectLessIcon.svg?react";
import SelectMoreIconSvg from "./icons/SelectMoreIcon.svg?react";
import SeparateIconSvg from "./icons/SeparateIcon.svg?react";
import ShrinkFattenIconSvg from "./icons/ShrinkFattenIcon.svg?react";
import SkinRadiusIconSvg from "./icons/SkinRadiusIcon.svg?react";
import SnapCursorToIconSvg from "./icons/SnapCursorToIcon.svg?react";
import SnapToCursorIconSvg from "./icons/SnapToCursorIcon.svg?react";
import SubdivideIconSvg from "./icons/SubdivideIcon.svg?react";
import SurfacePolyIconSvg from "./icons/SurfacePolyIcon.svg?react";
import ToggleCyclicIconSvg from "./icons/ToggleCyclicIcon.svg?react";
import VertexModeIconSvg from "./icons/VertexModeIcon.svg?react";
import ViewAxisIconSvg from "./icons/ViewAxisIcon.svg?react";
import WireframeIconSvg from "./icons/WireframeIcon.svg?react";
import XRayIconSvg from "./icons/XRayIcon.svg?react";

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

type SvgComponent = ComponentType<SVGProps<SVGSVGElement>>;

/** Wrap an imported SVG component in a Lucide-compatible forwardRef. */
function icon(displayName: string, SvgComp: SvgComponent) {
  const Icon = forwardRef<SVGSVGElement, LucideProps>(
    ({ size = 24, color, absoluteStrokeWidth: _, ...props }, ref) => (
      <SvgComp
        ref={ref}
        width={size}
        height={size}
        stroke={color ?? "currentColor"}
        {...props}
      />
    ),
  );
  Icon.displayName = displayName;
  return Icon;
}

// ---------------------------------------------------------------------------
// Mesh editing operators
// ---------------------------------------------------------------------------

/** Extrude — face pulled upward with directional arrow. */
export const ExtrudeIcon = icon("ExtrudeIcon", ExtrudeIconSvg);

/** Inset Faces — concentric quads with corner connections. */
export const InsetIcon = icon("InsetIcon", InsetIconSvg);

/** Bevel — sharp corner rounded into a smooth curve. */
export const BevelIcon = icon("BevelIcon", BevelIconSvg);

/** Fill — polygon filled with a face. */
export const FillIcon = icon("FillIcon", FillIconSvg);

/** Edge Slide — edge sliding between two rail edges. */
export const EdgeSlideIcon = icon("EdgeSlideIcon", EdgeSlideIconSvg);

/** Subdivide — quad split into four smaller quads. */
export const SubdivideIcon = icon("SubdivideIcon", SubdivideIconSvg);

/** Merge Vertices — four vertices converging into one. */
export const MergeVerticesIcon = icon(
  "MergeVerticesIcon",
  MergeVerticesIconSvg,
);

/** Insert Edge Loop — new edge loop cut across a face. */
export const EdgeLoopIcon = icon("EdgeLoopIcon", EdgeLoopIconSvg);

/** Surface Poly — straight-edged polygon footprint drawn on a host surface. */
export const SurfacePolyIcon = icon("SurfacePolyIcon", SurfacePolyIconSvg);

// ---------------------------------------------------------------------------
// Normals & geometry
// ---------------------------------------------------------------------------

/** Flip Normals — bidirectional arrow perpendicular to a surface. */
export const FlipNormalsIcon = icon("FlipNormalsIcon", FlipNormalsIconSvg);

/** Recalculate Outside — outward-pointing normals from a curved surface. */
export const RecalcOutsideIcon = icon(
  "RecalcOutsideIcon",
  RecalcOutsideIconSvg,
);

/** Connect Vertex Path — two vertices connected by a cutting edge through a face. */
export const ConnectPathIcon = icon("ConnectPathIcon", ConnectPathIconSvg);

/** Select Connected — vertex network showing linked selection. */
export const SelectConnectedIcon = icon(
  "SelectConnectedIcon",
  SelectConnectedIconSvg,
);

// ---------------------------------------------------------------------------
// Curve editing
// ---------------------------------------------------------------------------

/** Handle: Auto — smooth S-curve with automatic tangents. */
export const HandleAutoIcon = icon("HandleAutoIcon", HandleAutoIconSvg);

/** Handle: Vector — sharp angle with straight segments. */
export const HandleVectorIcon = icon("HandleVectorIcon", HandleVectorIconSvg);

/** Handle: Aligned — curve with collinear tangent handles. */
export const HandleAlignedIcon = icon(
  "HandleAlignedIcon",
  HandleAlignedIconSvg,
);

/** Handle: Free — curve with independent tangent handles. */
export const HandleFreeIcon = icon("HandleFreeIcon", HandleFreeIconSvg);

/** Toggle Cyclic — open curve with dashed closing segment. */
export const ToggleCyclicIcon = icon("ToggleCyclicIcon", ToggleCyclicIconSvg);

// ---------------------------------------------------------------------------
// Sculpt brushes
// ---------------------------------------------------------------------------

/** Brush: Draw — bold upward bump on a flat surface. */
export const BrushDrawIcon = icon("BrushDrawIcon", BrushDrawIconSvg);

/** Brush: Smooth — wavy line above a straight result. */
export const BrushSmoothIcon = icon("BrushSmoothIcon", BrushSmoothIconSvg);

/** Brush: Flatten — peak clipped by a horizontal plane. */
export const BrushFlattenIcon = icon("BrushFlattenIcon", BrushFlattenIconSvg);

/** Brush: Grab — surface pulled by an arrow. */
export const BrushGrabIcon = icon("BrushGrabIcon", BrushGrabIconSvg);

/** Brush: Inflate — dome expanding outward with directional arrows. */
export const BrushInflateIcon = icon("BrushInflateIcon", BrushInflateIconSvg);

/** Brush: Pinch — two inward arrows pulling a surface to a crease. */
export const BrushPinchIcon = icon("BrushPinchIcon", BrushPinchIconSvg);

/** Brush: Mask — dotted boundary enclosing a protected region. */
export const BrushMaskIcon = icon("BrushMaskIcon", BrushMaskIconSvg);

/** Brush: Relax — mesh grid evening out while preserving shape. */
export const BrushRelaxIcon = icon("BrushRelaxIcon", BrushRelaxIconSvg);

// ---------------------------------------------------------------------------
// View modes
// ---------------------------------------------------------------------------

/** Wireframe — cube rendered as edges only. */
export const WireframeIcon = icon("WireframeIcon", WireframeIconSvg);

/** Shaded sphere icon asset. */
export const MatcapIcon = icon("MatcapIcon", MatcapIconSvg);

/** Filled diamond icon asset. */
export const MaterialIcon = icon("MaterialIcon", MaterialIconSvg);

/**
 * Render-mode UI intentionally swaps the Material and Matcap glyphs.
 * Keep picker surfaces on these aliases instead of hardcoding the assets.
 */
export const MatcapRenderIcon = MaterialIcon;
export const MaterialRenderIcon = MatcapIcon;

/** X-Ray — cube with dashed hidden edges showing through. */
export const XRayIcon = icon("XRayIcon", XRayIconSvg);

// ---------------------------------------------------------------------------
// Transform panel
// ---------------------------------------------------------------------------

/** Pivot Point — crosshair with a center dot and rotation ring. */
export const PivotIcon = icon("PivotIcon", PivotIconSvg);

// ---------------------------------------------------------------------------
// Brush properties
// ---------------------------------------------------------------------------

/** Brush Size — concentric circles showing radius. */
export const BrushSizeIcon = icon("BrushSizeIcon", BrushSizeIconSvg);

/** Brush Strength — pressure gauge / intensity indicator. */
export const BrushStrengthIcon = icon(
  "BrushStrengthIcon",
  BrushStrengthIconSvg,
);

/** Brush Hardness — sharp-edged vs soft circle showing edge falloff. */
export const BrushHardnessIcon = icon(
  "BrushHardnessIcon",
  BrushHardnessIconSvg,
);

/** Pen Pressure — stylus with a tapered stroke showing pressure sensitivity. */
export const PenPressureIcon = icon("PenPressureIcon", PenPressureIconSvg);

/** Brush Opacity — circle with gradient-like fill indicating transparency. */
export const BrushOpacityIcon = icon("BrushOpacityIcon", BrushOpacityIconSvg);

// ---------------------------------------------------------------------------
// Edit mode granularity icons — vertex / edge / face
// ---------------------------------------------------------------------------

/** Vertex mode — a single prominent dot with faint surrounding grid vertices. */
export const VertexModeIcon = icon("VertexModeIcon", VertexModeIconSvg);

/** Edge mode — a highlighted edge between two vertices with faint quad context. */
export const EdgeModeIcon = icon("EdgeModeIcon", EdgeModeIconSvg);

/** Face mode — a filled quad with corner vertices. */
export const FaceModeIcon = icon("FaceModeIcon", FaceModeIconSvg);

/** Mesh menu — 2×2 wireframe grid with one filled face, representing vertex+edge+face together. */
export const MeshMenuIcon = icon("MeshMenuIcon", MeshMenuIconSvg);

// ---------------------------------------------------------------------------
// Selection icons
// ---------------------------------------------------------------------------

/** Random Select — scattered dots, some filled (selected) some hollow. */
export const RandomSelectIcon = icon("RandomSelectIcon", RandomSelectIconSvg);

// ---------------------------------------------------------------------------
// Modifier key icons
// ---------------------------------------------------------------------------

/** Alt key — Mac-style Option symbol (⌥). */
export const AltIcon = icon("AltIcon", AltIconSvg);

// ---------------------------------------------------------------------------
// Additional mesh operators
// ---------------------------------------------------------------------------

/** Shrink/Fatten — vertices moving outward along normals. */
export const ShrinkFattenIcon = icon("ShrinkFattenIcon", ShrinkFattenIconSvg);

/** Skin Radius — concentric rings at a vertex representing tube radius scale. */
export const SkinRadiusIcon = icon("SkinRadiusIcon", SkinRadiusIconSvg);

/** Grid Fill — closed loop filled with quad grid. */
export const GridFillIcon = icon("GridFillIcon", GridFillIconSvg);

/** Separate — geometry splitting into two pieces. */
export const SeparateIcon = icon("SeparateIcon", SeparateIconSvg);

/** Rip — vertex torn apart into two. */
export const RipIcon = icon("RipIcon", RipIconSvg);

/** Select More — selection expanding outward by one ring. */
export const SelectMoreIcon = icon("SelectMoreIcon", SelectMoreIconSvg);

/** Select Less — selection shrinking inward by one ring. */
export const SelectLessIcon = icon("SelectLessIcon", SelectLessIconSvg);

/** Select All — select every element. */
export const SelectAllIcon = icon("SelectAllIcon", SelectAllIconSvg);

/** Invert Selection — swap selected and unselected. */
export const InvertSelectionIcon = icon(
  "InvertSelectionIcon",
  InvertSelectionIconSvg,
);

/** Checker Deselect — keep alternating elements selected. */
export const CheckerDeselectIcon = icon(
  "CheckerDeselectIcon",
  CheckerSelectIconSvg,
);

/** Clear Selection — deselect all elements. */
export const ClearSelectionIcon = icon(
  "ClearSelectionIcon",
  ClearSelectionIconSvg,
);

// ---------------------------------------------------------------------------
// Multi-resolution
// ---------------------------------------------------------------------------

/** Multi-Resolution — surface shown at coarse and fine subdivision levels. */
export const MultiResIcon = icon("MultiResIcon", MultiResIconSvg);

// ---------------------------------------------------------------------------
// Transform orientation
// ---------------------------------------------------------------------------

/** Three-axis gizmo representing the current transform orientation space. */
export const OrientationIcon = icon("OrientationIcon", OrientationIconSvg);

// ---------------------------------------------------------------------------
// Cursor snap operations
// ---------------------------------------------------------------------------

/** Snap Selection to Cursor — selection moving to cursor location with entering arrow. */
export const SnapToCursorIcon = icon("SnapToCursorIcon", SnapToCursorIconSvg);

/** Snap Cursor to Selection — cursor moving to selection with exiting arrow. */
export const SnapCursorToIcon = icon("SnapCursorToIcon", SnapCursorToIconSvg);

// ---------------------------------------------------------------------------
// Viewport
// ---------------------------------------------------------------------------

/** View Axis — isometric cube representing axis-aligned view directions. */
export const ViewAxisIcon = icon("ViewAxisIcon", ViewAxisIconSvg);
